import React, { useState, useEffect, useMemo } from "react";
import { Button } from "antd";
import PickerSelect from "./Select";
import { IItemData } from "./common.typing";
import locale, { isZH } from "../../_util/locale";
import { getItemType } from "./utils";

//import "./index.less";

export interface IMainProps {
    onConfirm: (
        data: string[],
        length: number,
        groupCateList: string[],
        currentList: IItemData[]
    ) => void;
    currentList: IItemData[];
    maxNumber: number | undefined;
    searchPlaceholder: string | undefined;
    loadSearchData?: (key: string, rows?: number) => Promise<any>;
    loadSearchDataMore?: (
        type: string,
        key: string,
        rows: number
    ) => Promise<any>;

    searchData?: IItemData[];
    disabledids?: string[];

    setCurrentList: (list: IItemData[]) => void;
    isLoadingSearchData?: boolean;

    requiredList: IItemData[];
    searchType?: string[];
    /**
     * 是否展示确认按钮的数量
     * default：true
     */
    showSelectedNum?: boolean;
    /**
     * 确认按钮是否为 loading 态
     * default：false
     */
    isConfirmLoading?: boolean;
    totalCount: number;
    selectPanelBlur?: () => void;

    canSelectZero: boolean;
    /** 搜索预留内容区域 */
    mainChildren?: React.ReactNode;
    /** 是否只展示选中的数量,不显示最大数量 */
    justShowSelectedNoMax?: boolean;
    /** 埋点方法 */
    track: (eventName: string, eventData?: any) => void;
    /** 组件的标题 */
    title?: string;
}

const Main: React.FC<IMainProps> = ({
    onConfirm,
    currentList,
    setCurrentList,
    maxNumber,
    searchPlaceholder,
    loadSearchData,
    loadSearchDataMore,
    selectPanelBlur,
    searchData,
    disabledids,
    isLoadingSearchData,
    requiredList,
    searchType,
    totalCount,
    showSelectedNum,
    isConfirmLoading,
    canSelectZero,
    mainChildren,
    justShowSelectedNoMax = false,
    track,
    title,
}) => {

    /**
     * 获取类型排序权重：部门(1) < 群(2) < 人(3)
     */
    const getTypeOrder = (type: 'org' | 'team' | 'user'): number => {
        const orderMap = {
            'org': 1,
            'team': 2,
            'user': 3,
        };
        return orderMap[type] || 3;
    };

    const transToLabel = useMemo(() => {
        const requiredIds = requiredList.map(item => item.id);
        const list = currentList.filter(item => !requiredIds.includes(item.id));

        // 按照「部门」「群」「人」的顺序排序
        const sortedList = [...list].sort((a, b) => {
            const typeA = getItemType(a.id);
            const typeB = getItemType(b.id);
            const orderA = getTypeOrder(typeA);
            const orderB = getTypeOrder(typeB);

            // 如果类型相同，保持原有顺序
            if (orderA === orderB) {
                return 0;
            }
            return orderA - orderB;
        });

        return sortedList.map(v => {
            const { zh_cn, en_us } = v.i18n_name || {};
            const itemType = getItemType(v.id);
            return {
                key: v.id,
                value: v.id,
                label: (isZH() ? zh_cn : en_us) || v.name,
                type: itemType, // 添加类型标识
                pic: v.pic || '', // 添加图片属性
            };
        });
    }, [currentList, requiredList]);

    /**
     * 判断按钮是否可用
     */
    const isBtnDisabled = () => {
        const isDisabled =
            isConfirmLoading ||
            (!canSelectZero && totalCount === 0) ||
            (!!maxNumber && totalCount > maxNumber);
        return isDisabled;
    };

    const handleConfirm = () => {
        if (isBtnDisabled()) return;
        track("Click_Chat_Element", {
            pageName: "PSelect_001",
            chat_name: title,
            ResultsCount: totalCount,
            $element_name: "PSelect_001",
        });
        const list = currentList.map(item => item.id.toString());
        const group_cate_list = currentList.map(item =>
            String(item.group_cate)
        );
        onConfirm(list, totalCount, group_cate_list, currentList);
    };

    /**
     * 计算三种类型的数量
     */
    const getTypeCounts = () => {
        const requiredIds = requiredList.map(item => item.id);
        const list = currentList.filter(item => !requiredIds.includes(item.id));
        
        let orgCount = 0;
        let teamCount = 0;
        let userCount = 0;
        
        list.forEach(item => {
            const type = getItemType(item.id);
            if (type === 'org') {
                orgCount++;
            } else if (type === 'team') {
                teamCount++;
            } else if (type === 'user') {
                userCount++;
            }
        });
        
        return { orgCount, teamCount, userCount };
    };

    const genSelectedNumCom = () => {
        if (justShowSelectedNoMax) {
            return totalCount > 0 ? `(${totalCount})` : "";
        } else if (showSelectedNum && maxNumber) {
            return `(${totalCount}/${maxNumber})`;
        } else {
            return "";
        }
    };
    // 生成选中数量显示文本
    const genSelectedCountText = () => {
        const { orgCount, teamCount, userCount } = getTypeCounts();
        const parts: string[] = [];
        
        // 按照「部门」「群聊」「联系人」的顺序显示
        if (orgCount > 0) {
            parts.push(`${orgCount}${locale("selected_count_org_text")}`);
        }
        if (teamCount > 0) {
            parts.push(`${teamCount}${locale("selected_count_group_text")}`);
        }
        if (userCount > 0) {
            parts.push(`${userCount}${locale("selected_count_people_text")}`);
        }
        
        // 如果没有任何选中项，显示总数0
        if (parts.length === 0) {
            return "0";
        }
        
        // 用顿号连接各部分
        return parts.join("、");
    };

    return (
        <div className="complexPickerMain">
            <div className="selectedInfo">
                <div className="personNum">
                    {locale("selected")}
                    {genSelectedCountText()}
                </div>
            </div>
            {requiredList && !!requiredList.length && (
                <div className="requiredList">
                    {requiredList.map(v => (
                        <span className="labelName" key={v.id}>
                            {v.name}
                        </span>
                    ))}
                </div>
            )}
            <div className="searchcontent">
                <PickerSelect
                    {...{
                        searchPlaceholder,
                        loadSearchData,
                        loadSearchDataMore,
                        selectPanelBlur,
                        searchData,
                        disabledids,
                        curListLabel: transToLabel,
                        currentList,
                        setCurrentList,
                        isLoadingSearchData,
                        requiredList,
                        searchType
                    }}
                />
            </div>
            {mainChildren && (
                <>
                    <div className="devider"></div>
                    {mainChildren}
                </>
            )}
            <div className="buttonContentWrap">
                <Button
                    className={`confirmBtn ${
                        isBtnDisabled() ? "btnDisabled" : ""
                    }`}
                    type="primary"
                    onClick={handleConfirm}
                    loading={isConfirmLoading}
                >
                    <span>
                        {locale("common_ok")}
                        {genSelectedNumCom()}
                    </span>
                </Button>
            </div>
        </div>
    );
};

Main.defaultProps = {
    disabledids: [],
    showSelectedNum: true,
    isConfirmLoading: false,
};

export default Main;
